import { t } from "/WebInterface/new-ui/modules/localizations/localizations.js";
import TooltipTemplate from "./template.js";
import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";

const supportsAdoptedPositionSheets = () =>
  typeof CSSStyleSheet !== "undefined" &&
  typeof CSSStyleSheet.prototype.replaceSync === "function" &&
  "adoptedStyleSheets" in Document.prototype;

export default class Tooltip {
  constructor() {
    this.tooltipIdCounter = 0;
    this.outsideCloseHandler = null;
    this.positionSheet = null;
    this.tooltipBoxStyles = {};
    this.useAdoptedPositionSheets = supportsAdoptedPositionSheets();
  }

  init = () => {
    const tooltipsElem = _$("[data-tooltip]").sel();

    tooltipsElem.forEach((element) => {
      this.addTooltip(element);
    });
  };

  detachPositionSheet = () => {
    if (this.positionSheet) {
      document.adoptedStyleSheets = document.adoptedStyleSheets.filter(
        (s) => s !== this.positionSheet
      );
      this.positionSheet = null;
    }
    this.tooltipBoxStyles = {};
  };

  flushAdoptedTooltipRule = (tooltipId) => {
    if (!this.positionSheet || !tooltipId) return;
    const d = this.tooltipBoxStyles;
    const inner = Object.keys(d).length
      ? Object.entries(d)
          .map(([k, v]) => `${k}: ${v}`)
          .join("; ")
      : "";
    this.positionSheet.replaceSync(`#${CSS.escape(tooltipId)} { ${inner} }`);
  };

  setTooltipPosition = (
    position,
    element,
    tooltip,
    fixedPosition = false,
    intrinsicSizeMeasure = false
  ) => {
    const $tip = _$(tooltip);
    const tooltipId = tooltip.id;

    const applyInset = (partial) => {
      if (this.useAdoptedPositionSheets && this.positionSheet) {
        Object.assign(this.tooltipBoxStyles, partial);
        this.flushAdoptedTooltipRule(tooltipId);
      } else {
        $tip.css({
          top: "auto",
          left: "auto",
          right: "auto",
          bottom: "auto",
          ...partial,
        });
      }
    };

    const { bottom, left, right, top } = element.getBoundingClientRect();
    if (intrinsicSizeMeasure) {
      applyInset({
        top: "0",
        left: "-9999px",
        right: "auto",
        bottom: "auto",
      });
      void tooltip.offsetWidth;
    }
    const { width: tw, height: th } = tooltip.getBoundingClientRect();
    const centerX = left + (right - left) / 2 - tw / 2;
    const centerY = top + (bottom - top) / 2 - th / 2;

    const setPosition = (pos = "bottom") => {
      switch (pos) {
        case "bottom":
          applyInset({
            top: `${bottom + 10}px`,
            left: `${centerX}px`,
            right: "auto",
            bottom: "auto",
          });
          break;
        case "top": {
          const bottomVal = window.innerHeight - top + th + 10;
          applyInset({
            bottom: `${bottomVal}px`,
            left: `${centerX}px`,
            top: "auto",
            right: "auto",
          });
          break;
        }
        case "left":
          applyInset({
            top: `${centerY}px`,
            left: `${left - tw - 10}px`,
            right: "auto",
            bottom: "auto",
          });
          break;
        case "right":
          applyInset({
            top: `${centerY}px`,
            left: `${right + 10}px`,
            right: "auto",
            bottom: "auto",
          });
          break;
        default:
          break;
      }
    };

    setPosition(position);
    if (fixedPosition) return;

    const positions = ["bottom", "top", "left", "right"];
    positions.every((pos) => {
      if (pos === position) return true;
      const { clientHeight, clientWidth } = document.body;

      const { left: tl, right: tr, top: tt, bottom: tb } =
        tooltip.getBoundingClientRect();

      if (tl < 0 || tt < 0 || tr > clientWidth || tb > clientHeight) {
        setPosition(pos);
        const arrow = _$(tooltip).find(".arrow").el();
        if (arrow) {
          arrow.classList.remove(...positions);
          _$(arrow).addClass(pos);
        }
        return true;
      }
      return false;
    });
  };

  createTooltip = (
    element,
    tooltipText = "",
    position = "bottom",
    fixedPosition,
    closeTrigger = "hover",
    intrinsicSizeMeasure = false
  ) => {
    const text = tooltipText || element.getAttribute("data-tooltip");
    const dynamicData = element.getAttribute("data-tooltip-i18n-opt") || "{}";

    const tooltip = _$("<div>").addClass("text-tooltip");

    tooltip.html(
      TooltipTemplate({
        text: t(text, JSON.parse(dynamicData)),
        position,
      })
    );

    this.removeTooltip();

    const tooltipId = `tooltip-${Date.now()}-${++this.tooltipIdCounter}`;
    tooltip.attr("id", tooltipId);

    _$("body").append(tooltip.el());

    const tooltipEl = tooltip.el();
    tooltip.removeClass("d-n");
    tooltip.addClass("d-b");

    if (this.useAdoptedPositionSheets) {
      this.positionSheet = new CSSStyleSheet();
      document.adoptedStyleSheets = [
        ...document.adoptedStyleSheets,
        this.positionSheet,
      ];
      this.tooltipBoxStyles = {
        opacity: "0",
        top: "0",
        left: "0",
        right: "auto",
        bottom: "auto",
      };
      this.flushAdoptedTooltipRule(tooltipId);
    } else {
      _$(tooltipEl).css({
        top: "0",
        left: "0",
        right: "auto",
        bottom: "auto",
        opacity: "0",
      });
    }

    if (closeTrigger === "hover") {
      _$(element).on("mouseleave", () => {
        this.detachPositionSheet();
        tooltip.remove();
      });
    } else {
      this.outsideCloseHandler = (event) => {
        if (element.contains(event.target)) return;
        if (tooltipEl.contains(event.target)) return;
        this.removeTooltip();
      };
      setTimeout(() => {
        document.addEventListener("mousedown", this.outsideCloseHandler, true);
      }, 0);
    }

    this.setTooltipPosition(
      position,
      element,
      tooltipEl,
      fixedPosition,
      intrinsicSizeMeasure
    );

    if (this.useAdoptedPositionSheets && this.positionSheet) {
      this.tooltipBoxStyles.opacity = "1";
      this.flushAdoptedTooltipRule(tooltipId);
    } else {
      _$(tooltipEl).css("opacity", "1");
    }
  };

  addTooltip = ({
    element,
    tooltipText,
    position,
    fixedPosition,
    closeTrigger = "hover",
    intrinsicSizeMeasure = false,
  }) => {
    if (closeTrigger === "click") {
      _$(element).on("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (_$(".text-tooltip").sel().length) {
          this.removeTooltip();
          return;
        }
        this.createTooltip(
          element,
          tooltipText,
          position,
          fixedPosition,
          "click",
          intrinsicSizeMeasure
        );
      });
      return;
    }
    _$(element).on("mouseenter", () => {
      this.createTooltip(
        element,
        tooltipText,
        position,
        fixedPosition,
        "hover",
        intrinsicSizeMeasure
      );
    });
  };

  removeTooltip = () => {
    if (this.outsideCloseHandler) {
      document.removeEventListener(
        "mousedown",
        this.outsideCloseHandler,
        true
      );
      this.outsideCloseHandler = null;
    }
    this.detachPositionSheet();
    _$(".text-tooltip").remove();
  };
}
